import { ArrowRight, Boxes, CircleDollarSign, FilePenLine, ListChecks, ShieldAlert, Store, Tag, TriangleAlert } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState, ExecutionRail, LoadingState, MetricStrip, PageHeader, SectionHeading, StatusBadge, TaskTypeIcon, formatDate, typeLabels } from "../components";
import { useAppData } from "../context";

export function Dashboard() {
  const { data } = useAppData();
  const navigate = useNavigate();
  if (!data) return <LoadingState />;
  const total = data.tasks.reduce((sum, task) => sum + task.summary.total, 0);
  const success = data.tasks.reduce((sum, task) => sum + task.summary.success, 0);
  const failed = data.tasks.reduce((sum, task) => sum + task.summary.failed, 0);
  const review = data.tasks.reduce((sum, task) => sum + task.summary.review, 0);
  const accountRisk = data.accounts.filter((account) => account.status !== "ACTIVE").length;
  const processed = success + failed + review;
  const successRate = processed ? `${Math.round((success / processed) * 100)}%` : "--";

  return (
    <>
      <PageHeader eyebrow="OPERATIONS / OVERVIEW" title="批量作业总览" actions={<Link to="/repricing" className="button primary"><CircleDollarSign size={17} />查看调价进度</Link>} />
      <p className="shipping-rule-note">以下统计包含全部历史任务。查看本轮调价结果，请进入调价工作台。</p>
      <MetricStrip items={[
        { label: "任务", value: data.tasks.length, note: `${total} 条导入明细` },
        { label: "成功率", value: successRate, note: `${success} 条已回读`, tone: "positive" },
        { label: "失败", value: failed, note: "单条隔离", tone: failed ? "negative" : "" },
        { label: "待复核", value: review, note: "结果不确定", tone: review ? "warning" : "" },
        { label: "账号异常", value: accountRisk, note: `${data.accounts.length} 个已接入`, tone: accountRisk ? "warning" : "" },
      ]} />

      <section className="workspace-band rail-band">
        <SectionHeading title="执行轨道" meta="导入与平台回读分离" />
        <ExecutionRail tasks={data.tasks} />
      </section>

      <section className="workspace-band task-launchers">
        <SectionHeading title="选择批量功能" meta="一个文件对应一种任务类型" />
        <div className="launcher-grid">
          <button onClick={() => navigate("/repricing")}>
            <span className="launcher-code">NP</span><CircleDollarSign size={22} /><strong>调价工作台</strong><small>批次进度 · 价格对比 · 异常明细</small><ArrowRight size={17} />
          </button>
          <button onClick={() => navigate("/new-task?type=TITLE_UPDATE")}>
            <span className="launcher-code">TT</span><FilePenLine size={22} /><strong>批量改标题</strong><small>锁定条件预检</small><ArrowRight size={17} />
          </button>
          <button onClick={() => navigate("/new-task?type=INVENTORY_UPDATE")}>
            <span className="launcher-code">ST</span><Boxes size={22} /><strong>批量改库存</strong><small>库存清零二次确认</small><ArrowRight size={17} />
          </button>
          <button onClick={() => navigate("/new-task?type=DESCRIPTION_UPDATE")}>
            <span className="launcher-code">DS</span><ListChecks size={22} /><strong>批量改描述</strong><small>保留段落与换行</small><ArrowRight size={17} />
          </button>
          <button onClick={() => navigate("/new-task?type=LISTING_TYPE_UPDATE")}>
            <span className="launcher-code">LT</span><Tag size={22} /><strong>升级 Premium</strong><small>售价大于 15 且当前为 Classic</small><ArrowRight size={17} />
          </button>
        </div>
      </section>

      <div className="dashboard-grid">
        <section className="workspace-band">
          <SectionHeading title="最近任务" meta={`${data.tasks.length} 个任务`} action={<Link to="/tasks" className="text-link">全部任务<ArrowRight size={15} /></Link>} />
          {data.tasks.length ? <div className="table-scroll">
            <table className="data-table clickable-rows">
              <thead><tr><th>任务</th><th>类型</th><th>状态</th><th>进度</th><th>创建人</th><th>时间</th></tr></thead>
              <tbody>{data.tasks.slice(0, 6).map((task) => {
                const processedCount = task.summary.success + task.summary.failed + task.summary.review;
                const percent = task.summary.executable ? Math.round((processedCount / task.summary.executable) * 100) : 100;
                return <tr key={task.id} role="link" tabIndex={0} onClick={() => navigate(`/tasks/${task.id}`)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") navigate(`/tasks/${task.id}`); }}>
                  <td><strong className="mono">{task.taskNo}</strong><small>{task.note || "未填写任务说明"}</small></td>
                  <td><span className="type-cell"><TaskTypeIcon type={task.taskType} />{typeLabels[task.taskType]}</span></td>
                  <td><StatusBadge status={task.status} /></td>
                  <td><div className="progress-cell"><div><span style={{ width: `${percent}%` }} /></div><strong>{processedCount}/{task.summary.executable}</strong></div></td>
                  <td>{task.creator}</td><td>{formatDate(task.createdAt)}</td>
                </tr>;
              })}</tbody>
            </table>
          </div> : <EmptyState title="暂无真实任务记录" action={<Link to="/new-task" className="button secondary">运行文件预检</Link>} />}
        </section>

        <aside className="workspace-band account-watch">
          <SectionHeading title="账号健康" meta="授权与能力" action={<Link to="/accounts" className="icon-button" aria-label="查看账号" title="查看账号"><ArrowRight size={16} /></Link>} />
          {data.accounts.length ? <div className="health-list">
            {data.accounts.map((account) => <div key={account.id}>
              <span className={`health-icon ${account.status === "ACTIVE" ? "ok" : "warn"}`}>{account.status === "ACTIVE" ? <Store size={17} /> : <TriangleAlert size={17} />}</span>
              <div><strong>{account.accountCode}</strong><small>{account.mode} · {account.sites.join(", ") || "待检测"}</small></div>
              <StatusBadge status={account.status === "ACTIVE" ? "SUCCESS" : "NEEDS_REVIEW"} />
            </div>)}
          </div> : <EmptyState title="尚未接入真实授权账号" />}
          <div className="capability-note"><ShieldAlert size={18} /><span><strong>{data.writeEnabled ? "生产写入" : "生产保护"}</strong>{data.writeEnabled ? "已启用授权店铺批量任务，执行前仍会校验商品归属与目标站点。" : "只读 API 已接入，所有商品写入保持锁定。"}</span></div>
        </aside>
      </div>
    </>
  );
}
